import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * Les faisceaux : la seule animation du site qui tourne sans declenchement.
 * Tout le reste s anime au scroll ou au survol. C est ce qui les rend
 * signifiants — quatre cones qui balayent lentement, comme des poursuites
 * avant le lever de rideau.
 */
const BEAMS = [
  { className: "animate-sweep", style: { animationDuration: "22s" } },
  { className: "animate-sweep", style: { animationDuration: "28s", animationDelay: "-6s" }, warm: true },
  { className: "animate-sweep", style: { animationDuration: "34s", animationDelay: "-14s" } },
  { className: "animate-sweep", style: { animationDuration: "26s", animationDelay: "-20s" }, warm: true },
];

export function Hero() {
  const beamsRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * Parallaxe des faisceaux. On ecrit dans un rAF plutot que directement
     * dans le handler : le scroll se declenche bien plus souvent que le
     * navigateur ne peint, et modifier le style a chaque evenement provoque
     * des recalculs de layout inutiles.
     */
    let frame = 0;

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      if (beamsRef.current) {
        beamsRef.current.style.transform = `translateY(${y * 0.15}px)`;
        beamsRef.current.style.opacity = String(Math.max(0.25, 1 - y / 700));
      }
      if (hintRef.current) {
        hintRef.current.style.opacity = String(Math.max(0, 1 - y / 300));
      }
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="relative flex min-h-screen flex-col items-start justify-center overflow-hidden px-6 pb-20 pt-36 md:px-10">
      <div
        ref={beamsRef}
        aria-hidden="true"
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 900px 500px at 15% 20%, hsl(var(--brand) / 0.20), transparent 60%)," +
            "radial-gradient(ellipse 700px 600px at 85% 75%, hsl(var(--amber) / 0.14), transparent 60%)",
        }}
      >
        {BEAMS.map((beam, i) => (
          <div
            key={i}
            className={`absolute left-1/2 top-[-10%] h-[140%] w-0.5 origin-top blur-[1px] ${beam.className}`}
            style={{
              ...beam.style,
              background: beam.warm
                ? "linear-gradient(to bottom, hsl(var(--amber) / 0.28), transparent 70%)"
                : "linear-gradient(to bottom, hsl(var(--brand) / 0.35), transparent 70%)",
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-[760px]">
        <p className="mb-5 font-display text-[15px] text-brand">Bruxelles &amp; alentours</p>

        {/* Le wordmark en h1 : capitales, donc compatible avec la police maison. */}
        <h1 className="wordmark mb-7 text-[clamp(52px,9vw,108px)] leading-[0.95]">LUMOSPHERE</h1>

        <p className="mb-9 max-w-[520px] text-[19px] text-muted-foreground">
          Location de matériel lumière, créations sur-mesure et régie pour vos spectacles, théâtres
          et soirées. Un seul contact, de la conception à l&apos;allumage des projecteurs.
        </p>

        <div className="flex flex-wrap gap-4">
          <Button asChild>
            <Link to="/services">Réserver une date</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/location">Voir le matériel</Link>
          </Button>
        </div>
      </div>

      <div
        ref={hintRef}
        className="absolute bottom-9 left-6 z-10 flex items-center gap-2.5 text-[13px] text-muted-foreground md:left-10"
      >
        <span className="relative h-10 w-px overflow-hidden bg-hairline">
          <span className="absolute left-0 h-[40%] w-full animate-drip bg-brand" />
        </span>
        <span>Découvrir</span>
      </div>
    </section>
  );
}
